'use client';
import { faHeart as faHeartRegular } from '@fortawesome/pro-regular-svg-icons';
import { faCartShopping, faHeart } from '@fortawesome/pro-solid-svg-icons';
import { sendGTMEvent } from '@next/third-parties/google';
import { LazyMotion, m } from 'framer-motion';
import { FC, PointerEvent, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import FeatureFlag from '@/components/common/feature-flag/feature-flag';
import FormattedPrice from '@/components/common/formatted-price/formatted-price';
import { Button, Card, CardBody, CardFooter, Icon, IconButton, Image } from '@/components/ui';
import useCoreConfig from '@/hooks/use-core-config';
import useFavorites from '@/hooks/use-favorites/use-favorites';
import { useGetDiscountType } from '@/hooks/use-has-discount';
import useMyCart from '@/hooks/use-my-cart/use-my-cart';
import { useAppContext, useTranslation } from '@/providers/app-provider/app-provider';
import { useKeycloak } from '@/providers/keycloak-provider';
import {
  FavoritesActionButtonsEnum,
  MyCartActionButtonsEnum,
} from '@/types/action-buttons/action-buttons';
import { SimpleProductInfo } from '@/types/products/simple-product-info';
import { cn } from '@/utils';
import type { ContentVariant } from '@/utils/content-variant-from-background';
import loadFramerMotionFeatures from '@/utils/framer-motion-features';
import { buildGumletUrl } from '@/utils/gumlet';
import { PRODUCT_CARD_IMAGE_PARAMS } from '@/utils/product-listing-lcp-preload';
import { selectMainImage } from '@/utils/select-main-image';
import { ProductCardNavigationWrapper } from './product-card-navigation-wrapper';
import ProductCardStudioPreview from './product-card-studio-preview';

interface ProductCardProps {
  product: SimpleProductInfo;
  noMaxWidth?: boolean;
  /** When false, card click does not navigate and favorites are hidden (context-agnostic/preview mode) */
  interactive?: boolean;
  /** Show add-to-cart while `interactive` is false (e.g. studio product-card / tabbed grid preview) */
  showAddToCart?: boolean;
  /** Show favorites while `interactive` is false; still gated by `FeatureFlag feature="favorites"` */
  showFavorites?: boolean;
  /**
   * `light` (default): dark text on transparent/light surfaces.
   * `dark`: light text when the card sits on a dark section background (e.g. tabbed products grid).
   */
  contentVariant?: ContentVariant;
  addToCartButtonType?: MyCartActionButtonsEnum;
  /** First-row cards only: hints the browser to fetch this image before others (LCP candidate). */
  priority?: boolean;
}

enum ProductCardVersions {
  VERSION_1 = 'VERSION_1',
  VERSION_2 = 'VERSION_2',
  VERSION_3 = 'VERSION_3',
}

/** Wait before swapping to the secondary image so brief pointer passes don't flash. */
const HOVER_IMAGE_DELAY_MS = 400;

const ProductCard: FC<ProductCardProps> = props => {
  const { isStudioPreview } = useAppContext<{ isStudioPreview?: boolean } | undefined>() ?? {};
  if (isStudioPreview) {
    return <ProductCardStudioPreview {...props} />;
  }
  return <ProductCardFull {...props} />;
};

const useProductCardPresentation = (product: SimpleProductInfo, contentVariant: ContentVariant) => {
  const {
    variants: { productCard: productCardVersion },
    features,
    productCardStyle,
    currency,
  } = useCoreConfig();
  const { getDiscountType } = useGetDiscountType();
  const { keycloak } = useKeycloak();

  const isAuthenticated = keycloak?.authenticated;
  const shouldShowPrice = (!isAuthenticated && features.priceForGuests) || isAuthenticated;
  const shouldAllowCart = (!isAuthenticated && features.cartForGuests) || isAuthenticated;
  const shouldChangeImageOnHover = features.imageChangeOnHover;

  const isDarkSurface = contentVariant === 'dark';
  const primaryPriceColor = isDarkSurface
    ? 'white'
    : productCardVersion === ProductCardVersions.VERSION_2
      ? 'black'
      : 'gray';
  const mutedPriceColor = isDarkSurface ? 'whiteMuted' : 'gray';
  const strikethroughPriceColor = isDarkSurface
    ? 'whiteMuted'
    : productCardVersion === ProductCardVersions.VERSION_2
      ? 'black'
      : 'gray';
  const isVersion2ProductCard = productCardVersion === ProductCardVersions.VERSION_2;
  const productCardFontWeight = isVersion2ProductCard ? ('bold' as const) : undefined;

  const mainImageSrc = selectMainImage(product.images, PRODUCT_CARD_IMAGE_PARAMS);
  const hoverImageSrc =
    shouldChangeImageOnHover && product.images && product.images.length > 1
      ? buildGumletUrl(product.images[1], PRODUCT_CARD_IMAGE_PARAMS)
      : undefined;

  const renderPriceContent = () => {
    const discountType = getDiscountType(product, product.parentCategoryId, product.id);

    if (discountType === 'NONE') {
      return (
        <FormattedPrice
          price={product.price}
          color={productCardStyle?.priceColor ? undefined : primaryPriceColor}
          customColor={productCardStyle?.priceColor}
          productId={product.id}
          fontWeight={productCardFontWeight}
          showVatSuffix
          vatSuffixSize="sm"
          vatSuffixColor={productCardStyle?.vatBadgeColor}
          vatSuffixBackground={productCardStyle?.vatBadgeBackground}
        />
      );
    }

    if (discountType === 'PRODUCT') {
      return (
        <>
          <FormattedPrice
            price={product.discountedPrice}
            color="red"
            productId={product.id}
            fontWeight={productCardFontWeight}
            showVatSuffix
            vatSuffixSize="sm"
            vatSuffixColor={productCardStyle?.vatBadgeColor}
            vatSuffixBackground={productCardStyle?.vatBadgeBackground}
          />
          <FormattedPrice
            price={product.price}
            color={strikethroughPriceColor}
            fontWeight={productCardFontWeight}
            lineThrough
            productId={product.id}
            showVatSuffix={false}
          />
        </>
      );
    }

    if (discountType === 'PRICE_LIST') {
      return (
        <>
          <FormattedPrice
            price={product.price}
            color="red"
            productId={product.id}
            fontWeight={productCardFontWeight}
            showVatSuffix
            vatSuffixSize="sm"
            vatSuffixColor={productCardStyle?.vatBadgeColor}
            vatSuffixBackground={productCardStyle?.vatBadgeBackground}
          />
          <FormattedPrice
            price={product.price}
            color={strikethroughPriceColor}
            fontWeight={productCardFontWeight}
            lineThrough
            productId={product.id}
            showVatSuffix={false}
          />
        </>
      );
    }

    return (
      <>
        <FormattedPrice
          price={product.price}
          color="red"
          categoryId={product.parentCategoryId}
          productId={product.id}
          fontWeight={productCardFontWeight}
          showVatSuffix
          vatSuffixSize="sm"
          vatSuffixColor={productCardStyle?.vatBadgeColor}
          vatSuffixBackground={productCardStyle?.vatBadgeBackground}
        />
        <FormattedPrice
          price={product.price}
          color={mutedPriceColor}
          fontWeight={productCardFontWeight}
          lineThrough
          productId={product.id}
          showVatSuffix={false}
        />
      </>
    );
  };

  return {
    productCardVersion,
    productCardStyle,
    currency,
    shouldShowPrice,
    shouldAllowCart,
    shouldChangeImageOnHover,
    isDarkSurface,
    isVersion2ProductCard,
    mainImageSrc,
    hoverImageSrc,
    renderPriceContent,
  };
};

const useMobileViewport = () => {
  const [isMobileViewport, setIsMobileViewport] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const update = () => setIsMobileViewport(mediaQuery.matches);
    update();
    mediaQuery.addEventListener('change', update);
    return () => mediaQuery.removeEventListener('change', update);
  }, []);

  return isMobileViewport;
};

const useHoverImage = (shouldChangeImageOnHover: boolean, hoverImageSrc?: string) => {
  const [isHovering, setIsHovering] = useState(false);
  const hoverDelayRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearHoverDelay = () => {
    if (hoverDelayRef.current != null) {
      clearTimeout(hoverDelayRef.current);
      hoverDelayRef.current = null;
    }
  };

  useEffect(
    () => () => {
      if (hoverDelayRef.current != null) {
        clearTimeout(hoverDelayRef.current);
        hoverDelayRef.current = null;
      }
    },
    [],
  );

  const pointerHandlers = {
    onPointerEnter: (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return;
      if (!shouldChangeImageOnHover || !hoverImageSrc) return;
      clearHoverDelay();
      hoverDelayRef.current = setTimeout(() => setIsHovering(true), HOVER_IMAGE_DELAY_MS);
    },
    onPointerLeave: (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return;
      clearHoverDelay();
      if (shouldChangeImageOnHover) {
        setIsHovering(false);
      }
    },
  };

  return { isHovering, pointerHandlers };
};

const ProductCardFull: FC<ProductCardProps> = ({
  product,
  noMaxWidth,
  interactive = true,
  showAddToCart = false,
  showFavorites = false,
  contentVariant = 'light',
  addToCartButtonType = MyCartActionButtonsEnum.PRODUCT_CARD,
  priority = false,
}) => {
  const { t } = useTranslation();
  const { addProductToMyCart } = useMyCart();
  const { addProductToFavorites, removeProductFromFavorites, favorites, isFavoritePending } =
    useFavorites();
  const isMobileViewport = useMobileViewport();
  const {
    productCardVersion,
    productCardStyle,
    currency,
    shouldShowPrice,
    shouldAllowCart,
    shouldChangeImageOnHover,
    isDarkSurface,
    isVersion2ProductCard,
    mainImageSrc,
    hoverImageSrc,
    renderPriceContent,
  } = useProductCardPresentation(product, contentVariant);
  const { isHovering, pointerHandlers } = useHoverImage(shouldChangeImageOnHover, hoverImageSrc);

  const isInFavorites = !!favorites.products.find(p => p.id === product.id);
  const showCartButton = (interactive || showAddToCart) && (shouldAllowCart || showAddToCart);
  const isFavoriteCardPending = isFavoritePending(
    product.id,
    FavoritesActionButtonsEnum.PRODUCT_CARD,
  );
  const favoriteAriaLabel = isInFavorites
    ? `Remove ${product.name} from favorites`
    : `Add ${product.name} to favorites`;

  return (
    <LazyMotion features={loadFramerMotionFeatures} strict>
      <ProductCardNavigationWrapper
        href={`/${product.locale}/product/${product.meta.url}`}
        interactive={interactive}
      >
        <Card
          className={cn(
            'relative h-full min-h-[340px] overflow-hidden rounded-2xl bg-transparent p-0 shadow-none @lg:min-h-0 @lg:p-2',
            interactive && 'cursor-pointer',
            {
              'bg-default-50': productCardVersion === ProductCardVersions.VERSION_1,
              'max-w-[258px]': !noMaxWidth,
            },
          )}
          data-testid="product-card__card"
        >
          {(interactive || showFavorites) && (
            <FeatureFlag feature="favorites">
              <div className="absolute top-3 right-3 z-30">
                <IconButton
                  aria-label={favoriteAriaLabel}
                  data-testid="product-card__wishlist-button"
                  onClick={e => {
                    e.preventDefault();
                    if (isInFavorites) {
                      const currentEventId = crypto.randomUUID();
                      sendGTMEvent({ ecommerce: null });
                      sendGTMEvent({
                        event: 'remove_from_favorites',
                        event_id: currentEventId,
                        ecommerce: {
                          currency,
                          value: product.discountedPrice,
                          items: [
                            {
                              item_id: product.id,
                              item_name: product.name,
                              price: product.discountedPrice,
                              item_category: product.parentCategoryId || '',
                            },
                          ],
                        },
                      });
                      removeProductFromFavorites(
                        product,
                        {
                          name: 'RemoveFromWishlist',
                          id: currentEventId,
                          sourceUrl: window.location.href,
                        },
                        FavoritesActionButtonsEnum.PRODUCT_CARD,
                      );
                    } else {
                      const currentEventId = crypto.randomUUID();
                      sendGTMEvent({ ecommerce: null });
                      sendGTMEvent({
                        event: 'add_to_favorites',
                        event_id: currentEventId,
                        ecommerce: {
                          currency,
                          value: product.discountedPrice,
                          items: [
                            {
                              item_id: product.id,
                              item_name: product.name,
                              price: product.discountedPrice,
                              item_category: product.parentCategoryId || '',
                            },
                          ],
                        },
                      });
                      addProductToFavorites(
                        product,
                        {
                          name: 'AddToWishlist',
                          id: currentEventId,
                          sourceUrl: window.location.href,
                        },
                        FavoritesActionButtonsEnum.PRODUCT_CARD,
                      );
                    }
                  }}
                  className="bg-white"
                  variant="flat"
                  isLoading={isFavoriteCardPending}
                >
                  <m.div whileTap={{ scale: 1.4 }}>
                    <Icon
                      icon={isInFavorites ? faHeart : faHeartRegular}
                      className={cn('!text-base', { 'text-red-600': isInFavorites })}
                    />
                  </m.div>
                </IconButton>
              </div>
            </FeatureFlag>
          )}
          <CardBody className="overflow-visible p-0">
            <div
              className="relative aspect-square w-full overflow-hidden rounded-2xl"
              data-testid="product-card__image-container"
              {...pointerHandlers}
            >
              <m.div
                className="h-full w-full"
                animate={{ opacity: isHovering && hoverImageSrc ? 0 : 1 }}
                transition={{ duration: 0.3 }}
              >
                <Image
                  src={mainImageSrc}
                  alt={product.name}
                  className="h-full w-full object-cover"
                  classNames={{
                    wrapper: 'h-full w-full !max-w-full',
                    img: 'h-full w-full object-cover',
                  }}
                  {...(priority ? { fetchPriority: 'high', loading: 'eager' } : {})}
                  data-testid="product-card__image"
                  radius="none"
                />
              </m.div>
              {hoverImageSrc && (
                <m.div
                  className="absolute inset-0 h-full w-full"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: isHovering ? 1 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Image
                    src={hoverImageSrc}
                    alt={product.name}
                    className="h-full w-full object-cover"
                    classNames={{
                      wrapper: 'h-full w-full !max-w-full',
                      img: 'h-full w-full object-cover',
                    }}
                    radius="none"
                  />
                </m.div>
              )}
            </div>
            <div className="flex flex-col gap-1 px-2 py-3" data-testid="product-card__details">
              <p
                className={cn(
                  'line-clamp-2 text-sm font-medium',
                  !productCardStyle?.titleColor && (isDarkSurface ? 'text-white' : 'text-black'),
                  {
                    'font-bold': isVersion2ProductCard,
                  },
                )}
                style={
                  productCardStyle?.titleColor ? { color: productCardStyle.titleColor } : undefined
                }
                title={product.name}
                data-testid="product-card__title"
              >
                {product.name}
              </p>

              <div
                className={cn('flex items-center gap-2', { hidden: !shouldShowPrice })}
                data-testid="product-card__price-and-button"
              >
                {renderPriceContent()}
              </div>
            </div>
          </CardBody>

          {showCartButton && (
            <CardFooter className="px-2 pt-0 pb-3">
              <Button
                color="primary"
                className="w-full min-w-0"
                size={isMobileViewport ? 'sm' : 'md'}
                data-testid="product-card__add-to-cart-icon-button"
                isDisabled={showAddToCart && !shouldAllowCart}
                onClick={e => {
                  const currentEventId = crypto.randomUUID();
                  sendGTMEvent({ ecommerce: null });
                  sendGTMEvent({
                    event: 'add_to_cart',
                    event_id: currentEventId,
                    ecommerce: {
                      currency,
                      value: product.discountedPrice,
                      items: [
                        {
                          item_id: product.id,
                          item_name: product.name,
                          price: product.discountedPrice,
                          quantity: 1,
                          item_category: product.parentCategoryId || '',
                        },
                      ],
                    },
                  });
                  e.preventDefault();
                  e.stopPropagation();
                  if (!shouldAllowCart) {
                    return;
                  }
                  addProductToMyCart(
                    { ...product, quantity: 1 },
                    {
                      name: 'AddToCart',
                      id: currentEventId,
                      sourceUrl: window.location.href,
                    },
                    addToCartButtonType,
                  );
                  if (addToCartButtonType === MyCartActionButtonsEnum.CROSS_UPSELL) {
                    toast.success(t?.common.crossUpsellDialog?.addedToCart);
                  }
                }}
              >
                {t?.common.addToCart}
                <Icon icon={faCartShopping} className="ml-2" />
              </Button>
            </CardFooter>
          )}
        </Card>
      </ProductCardNavigationWrapper>
    </LazyMotion>
  );
};

export default ProductCard;
